import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import TrackCard from "@/components/TrackCard";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { ChevronDown, Heart, ListMusic, Music2, Play, Plus, Search, Shuffle, Trash2, X } from "lucide-react";

export default function MyMusic() {
  const { user } = useAuth();
  const { playTrack, shuffle, setShuffle, shuffleSource, setShuffleSource } = usePlayer();
  const [tracks, setTracks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [activePlaylistId, setActivePlaylistId] = useState("");
  const [newName, setNewName] = useState("");
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [collapsedGenres, setCollapsedGenres] = useState({});
  const initializedCollapsedGenres = useRef(false);
  const libraryRef = useRef(null);
  const playlistsRef = useRef(null);

  const favoriteIdSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);
  const favoritePlaylist = useMemo(() => ({
    id: "__favorites__",
    name: "Favorite",
    tracks: favorites,
    virtual: true,
  }), [favorites]);
  const selectablePlaylists = useMemo(() => user ? [favoritePlaylist, ...playlists] : [], [user, favoritePlaylist, playlists]);
  const activePlaylist = selectablePlaylists.find((p) => p.id === activePlaylistId) || selectablePlaylists[0] || null;
  const activeWritablePlaylistId = activePlaylist?.virtual ? (playlists[0]?.id || "") : (activePlaylist?.id || "");
  const playlistMembershipByTrack = useMemo(() => {
    const map = new Map();
    playlists.forEach((playlist) => {
      (playlist.tracks || []).forEach((track) => {
        if (!map.has(track.id)) map.set(track.id, []);
        map.get(track.id).push({ id: playlist.id, name: playlist.name });
      });
    });
    return map;
  }, [playlists]);

  const visibleTracks = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return tracks;
    return tracks.filter((t) => `${t.title} ${t.artist} ${t.genre} ${t.mood}`.toLowerCase().includes(needle));
  }, [q, tracks]);

  const genreColumns = useMemo(() => {
    const grouped = new Map();
    visibleTracks.forEach((track) => {
      const key = track.genre || "Altele";
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(track);
    });
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b, "ro"));
  }, [visibleTracks]);

  useEffect(() => {
    if (initializedCollapsedGenres.current || !genreColumns.length) return;
    setCollapsedGenres(Object.fromEntries(genreColumns.map(([genre]) => [genre, true])));
    initializedCollapsedGenres.current = true;
  }, [genreColumns]);

  const toggleGenre = (genre) => {
    setCollapsedGenres((prev) => ({ ...prev, [genre]: !prev[genre] }));
  };

  const collapseAllGenres = () => {
    setCollapsedGenres(Object.fromEntries(genreColumns.map(([genre]) => [genre, true])));
  };

  const expandAllGenres = () => {
    setCollapsedGenres({});
  };

  const playCollection = (collectionTracks, sourceId = "") => {
    if (!collectionTracks.length) return;
    const sourceShuffleActive = !!sourceId && shuffle && shuffleSource === sourceId;
    const start = sourceShuffleActive
      ? (collectionTracks[Math.floor(Math.random() * collectionTracks.length)] || collectionTracks[0])
      : collectionTracks[0];
    if (!sourceShuffleActive) {
      setShuffle(false);
      setShuffleSource("");
    }
    playTrack(start, collectionTracks, { shuffleSource: sourceShuffleActive ? sourceId : "" });
  };

  const toggleCollectionShuffle = (sourceId) => {
    const isActive = shuffle && shuffleSource === sourceId;
    setShuffle(!isActive);
    setShuffleSource(isActive ? "" : sourceId);
  };

  const scrollToMusicSection = (section) => {
    const target = section === "playlists" ? playlistsRef.current : libraryRef.current;
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const loadWorkspace = async () => {
    setLoading(true);
    try {
      try {
        const trackRes = await api.get("/tracks", {
          params: { sort: "new", limit: 2000, _: Date.now() },
          headers: { "Cache-Control": "no-cache" },
        });
        setTracks(trackRes.data || []);
      } catch (error) {
        const detail = error?.response?.status
          ? `Cod ${error.response.status}`
          : (error?.message || "eroare de retea");
        console.error("Nu am putut incarca muzica", { api: API, error });
        toast.error(`Nu am putut incarca muzica. ${detail}`);
        setTracks([]);
      }

      if (user) {
        const [favRes, playlistRes] = await Promise.allSettled([
          api.get("/me/favorites"),
          api.get("/me/playlists"),
        ]);
        if (favRes.status === "fulfilled") {
          setFavorites(favRes.value.data || []);
          setFavoriteIds((favRes.value.data || []).map((t) => t.id));
        } else {
          setFavorites([]);
          setFavoriteIds([]);
          toast.message("Muzica s-a incarcat, dar favoritele nu au putut fi citite.");
        }
        if (playlistRes.status === "fulfilled") {
          setPlaylists(playlistRes.value.data || []);
        } else {
          setPlaylists([]);
          toast.message("Muzica s-a incarcat, dar playlisturile personale nu au putut fi citite.");
        }
        setActivePlaylistId("__favorites__");
      } else {
        setFavorites([]);
        setFavoriteIds([]);
        setPlaylists([]);
        setActivePlaylistId("");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWorkspace(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = async (track) => {
    try {
      const { data } = await api.post(`/me/favorites/${track.id}/toggle`);
      setFavoriteIds(data.favorite_track_ids || []);
      if (data.is_favorite) {
        setFavorites((prev) => prev.some((x) => x.id === track.id) ? prev : [track, ...prev]);
      } else {
        setFavorites((prev) => prev.filter((x) => x.id !== track.id));
      }
    } catch {
      toast.error("Nu am putut actualiza favoritele.");
    }
  };

  const createPlaylist = async () => {
    if (!user) {
      toast.error("Autentifica-te pentru a crea playlisturi.");
      return;
    }
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/me/playlists", { name });
      setPlaylists((prev) => [data, ...prev]);
      setActivePlaylistId(data.id);
      setNewName("");
      setCreatingPlaylist(false);
      toast.success("Playlist creat. Poti adauga piese imediat.");
    } catch {
      toast.error("Nu am putut crea playlistul.");
    }
  };

  const addToPlaylist = async (playlistId, track) => {
    if (!user) {
      toast.error("Autentifica-te pentru a salva playlisturi.");
      return;
    }
    const targetId = playlistId || activePlaylist?.id;
    if (!targetId) {
      toast.error("Creeaza intai un playlist.");
      return;
    }
    if (targetId === "__favorites__") {
      await toggleFavorite(track);
      return;
    }
    try {
      const { data } = await api.post(`/me/playlists/${targetId}/tracks/${track.id}`);
      setPlaylists((prev) => prev.map((p) => p.id === targetId ? data : p));
      setActivePlaylistId(targetId);
    } catch {
      toast.error("Nu am putut adauga piesa.");
    }
  };

  const removeFromPlaylist = async (playlistId, trackId) => {
    try {
      const { data } = await api.delete(`/me/playlists/${playlistId}/tracks/${trackId}`);
      setPlaylists((prev) => prev.map((p) => p.id === playlistId ? data : p));
    } catch {
      toast.error("Nu am putut elimina piesa.");
    }
  };

  const deletePlaylist = async (playlistId) => {
    try {
      await api.delete(`/me/playlists/${playlistId}`);
      setPlaylists((prev) => {
        const next = prev.filter((p) => p.id !== playlistId);
        setActivePlaylistId(next[0]?.id || "");
        return next;
      });
    } catch {
      toast.error("Nu am putut sterge playlistul.");
    }
  };

  const renamePlaylist = async (playlist, name) => {
    const clean = name.trim();
    if (!clean || clean === playlist.name) return;
    try {
      const { data } = await api.put(`/me/playlists/${playlist.id}`, { name: clean });
      setPlaylists((prev) => prev.map((p) => p.id === playlist.id ? data : p));
    } catch {
      toast.error("Nu am putut redenumi playlistul.");
    }
  };

  if (loading) {
    return <div className="mx-auto max-w-7xl px-4 pb-32 pt-10 text-zinc-400">Se incarca muzica...</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-3 pb-32 pt-5 sm:px-6 lg:px-8">
      <header className="mb-5 sm:mb-7">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4A23A]">Muzica mea</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight sm:text-5xl">Biblioteca live</h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-400">
            Cauta, asculta si construieste playlistul in acelasi ecran.
          </p>
        </div>
      </header>

      <div className="sticky top-16 z-30 mb-4 rounded-2xl border border-white/10 bg-[#0A0A0A]/95 p-1.5 backdrop-blur-xl lg:hidden" data-testid="mobile-music-section-nav">
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => scrollToMusicSection("library")}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#D4A23A] px-3 py-3 text-sm font-black text-black"
          >
            <Music2 size={16} /> Biblioteca
          </button>
          <button
            type="button"
            onClick={() => scrollToMusicSection("playlists")}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#D4A23A]/35 bg-[#D4A23A]/10 px-3 py-3 text-sm font-black text-[#D4A23A]"
          >
            <ListMusic size={16} /> Playlisturi
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_390px]">
        <section ref={libraryRef} className="scroll-mt-36 min-w-0 rounded-2xl border border-white/5 bg-[#101010] p-3 sm:p-4 lg:scroll-mt-24">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row">
            <div className="lime-focus flex flex-1 items-center gap-2 rounded-full border border-white/10 bg-black/25 px-4 py-2.5">
              <Search size={16} className="text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cauta piesa, artist, gen..."
                className="w-full bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={() => scrollToMusicSection("playlists")}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4A23A] px-4 py-2.5 text-sm font-black text-black transition hover:bg-[#E2B85C] lg:hidden"
            >
              <ListMusic size={15} /> Playlisturi
            </button>
            <button
              onClick={() => favorites.length && playTrack(favorites[0], favorites)}
              disabled={!favorites.length}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-sm font-semibold text-zinc-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Heart size={15} /> Favorite
            </button>
          </div>

          <div className="mb-3 flex flex-col gap-2 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span>{visibleTracks.length} piese disponibile</span>
              <span className="hidden text-zinc-700 sm:inline">/</span>
              <button type="button" onClick={expandAllGenres} className="rounded-full border border-white/10 px-2 py-1 text-zinc-300 transition hover:bg-white/5 hover:text-white">
                Extinde toate
              </button>
              <button type="button" onClick={collapseAllGenres} className="rounded-full border border-white/10 px-2 py-1 text-zinc-300 transition hover:bg-white/5 hover:text-white">
                Comprima toate
              </button>
            </div>
            <span>{activeWritablePlaylistId ? `Adaugi in: ${playlists.find((p) => p.id === activeWritablePlaylistId)?.name || "playlist"}` : user ? "Favoritele se gestioneaza cu inimioara" : "Autentifica-te pentru playlisturi"}</span>
          </div>

          {visibleTracks.length === 0 ? (
            <Empty icon={Music2} text="Nu am gasit piese pentru cautarea aceasta." />
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {genreColumns.map(([genre, genreTracks]) => (
                <section key={genre} className="min-w-0 rounded-2xl border border-white/5 bg-black/20 p-2">
                  <div className="sticky top-16 z-10 mb-2 rounded-xl bg-[#101010]/95 px-2 py-2 backdrop-blur">
                    <button
                      type="button"
                      onClick={() => toggleGenre(genre)}
                      className="flex w-full items-center justify-between gap-2 text-left"
                      aria-expanded={!collapsedGenres[genre]}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <ChevronDown size={15} className={`shrink-0 text-[#D4A23A] transition-transform ${collapsedGenres[genre] ? "-rotate-90" : "rotate-0"}`} />
                        <h3 className="truncate font-display text-sm font-bold text-white">{genre}</h3>
                      </span>
                      <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-zinc-500">{genreTracks.length}</span>
                    </button>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => playCollection(genreTracks, `genre:${genre}`)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#D4A23A] px-2 py-1.5 text-xs font-black text-black transition hover:bg-[#E2B85C]"
                      >
                        <Play size={12} fill="currentColor" /> Reda
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleCollectionShuffle(`genre:${genre}`)}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-full px-2 py-1.5 text-xs font-black transition ${
                          shuffle && shuffleSource === `genre:${genre}`
                            ? "bg-[#D4A23A] text-black shadow-[0_0_20px_rgba(212,162,58,0.18)]"
                            : "border border-[#D4A23A]/35 bg-[#D4A23A]/10 text-[#D4A23A] hover:bg-[#D4A23A] hover:text-black"
                        }`}
                      >
                        <Shuffle size={12} /> Shuffle
                      </button>
                    </div>
                  </div>
                  {!collapsedGenres[genre] && (
                    <div className="space-y-2">
                      {genreTracks.map((track, i) => (
                        <TrackCard
                          key={track.id}
                          track={track}
                          list={genreTracks}
                          index={i}
                          isFavorite={favoriteIdSet.has(track.id)}
                          playlistMemberships={playlistMembershipByTrack.get(track.id) || []}
                          onToggleFavorite={user ? toggleFavorite : undefined}
                          playlists={playlists}
                          activePlaylistId={activeWritablePlaylistId}
                          onAddToPlaylist={user && playlists.length ? addToPlaylist : undefined}
                        />
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </section>

        <aside ref={playlistsRef} className="scroll-mt-36 min-w-0 rounded-2xl border border-[#D4A23A]/20 bg-[#16130B] p-3 sm:p-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-auto lg:scroll-mt-24">
          <div className="mb-4 flex items-center gap-2">
            <ListMusic size={18} className="text-[#D4A23A]" />
            <h2 className="font-display text-xl font-bold">Playlist live</h2>
          </div>

          <div className="mb-4">
            {!creatingPlaylist ? (
              <button
                type="button"
                onClick={() => {
                  setCreatingPlaylist(true);
                  setNewName("");
                }}
                disabled={!user}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D4A23A] px-4 py-2.5 text-sm font-black text-black transition hover:bg-[#E2B85C] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus size={16} /> Creeaza playlist
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") createPlaylist();
                    if (e.key === "Escape") {
                      setCreatingPlaylist(false);
                      setNewName("");
                    }
                  }}
                  autoFocus
                  disabled={!user}
                  className="min-w-0 flex-1 rounded-full border border-white/10 bg-black/25 px-4 py-2 text-sm text-white outline-none focus:border-[#D4A23A]/60"
                  placeholder="Nume playlist"
                />
                <button onClick={createPlaylist} disabled={!user || !newName.trim()} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#D4A23A] text-black disabled:cursor-not-allowed disabled:opacity-40">
                  <Plus size={17} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCreatingPlaylist(false);
                    setNewName("");
                  }}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 text-zinc-400 transition hover:bg-white/5 hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {selectablePlaylists.length > 0 && (
            <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
              {selectablePlaylists.map((playlist) => (
                <button
                  key={playlist.id}
                  onClick={() => setActivePlaylistId(playlist.id)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    activePlaylist?.id === playlist.id ? "bg-[#D4A23A] text-black" : "border border-white/10 text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  {playlist.name} ({playlist.tracks?.length || 0})
                </button>
              ))}
            </div>
          )}

          {!user ? (
            <Empty icon={ListMusic} text="Autentifica-te pentru a salva playlisturi personale." />
          ) : !activePlaylist ? (
            <Empty icon={ListMusic} text="Creeaza un playlist si incepe sa adaugi piese din biblioteca." />
          ) : activePlaylist.virtual ? (
            <>
              <div className="mb-3 flex items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Heart size={17} className="shrink-0 text-[#D4A23A]" />
                  <div className="min-w-0">
                    <h3 className="truncate font-display text-lg font-bold text-white">Favorite</h3>
                    <p className="text-xs text-zinc-500">{favorites.length} piese salvate</p>
                  </div>
                </div>
                <button
                  onClick={() => playCollection(favorites, "favorites")}
                  disabled={!favorites.length}
                  className="rounded-full bg-[#D4A23A] px-3 py-2 text-xs font-black text-black disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Reda
                </button>
                <button
                  onClick={() => toggleCollectionShuffle("favorites")}
                  disabled={!favorites.length}
                  className={`rounded-full px-3 py-2 text-xs font-black transition disabled:cursor-not-allowed disabled:bg-[#D4A23A]/20 disabled:text-[#D4A23A] disabled:opacity-50 ${
                    shuffle && shuffleSource === "favorites"
                      ? "bg-[#D4A23A] text-black shadow-[0_0_20px_rgba(212,162,58,0.18)] hover:bg-[#E2B85C]"
                      : "border border-[#D4A23A]/35 bg-[#D4A23A]/10 text-[#D4A23A] hover:bg-[#D4A23A] hover:text-black"
                  }`}
                >
                  Shuffle
                </button>
              </div>

              {!favorites.length ? (
                <div className="rounded-2xl border border-dashed border-[#D4A23A]/20 p-6 text-center text-sm text-zinc-400">
                  Apasa inimioara pe piesele preferate. Vor aparea aici automat.
                </div>
              ) : (
                <div className="space-y-2">
                  {favorites.map((track, i) => (
                    <TrackCard
                      key={`favorites-${track.id}`}
                      track={track}
                      list={favorites}
                      index={i}
                      isFavorite={favoriteIdSet.has(track.id)}
                      playlistMemberships={playlistMembershipByTrack.get(track.id) || []}
                      onToggleFavorite={toggleFavorite}
                      playlists={playlists}
                      activePlaylistId={playlists[0]?.id || ""}
                      onAddToPlaylist={playlists.length ? addToPlaylist : undefined}
                      showPlaylistActions={!!playlists.length}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-3 flex items-center gap-2">
                <input
                  value={activePlaylist.name}
                  onChange={(e) => setPlaylists((prev) => prev.map((p) => p.id === activePlaylist.id ? { ...p, name: e.target.value } : p))}
                  onBlur={(e) => renamePlaylist(activePlaylist, e.target.value)}
                  className="min-w-0 flex-1 bg-transparent font-display text-lg font-bold text-white outline-none"
                />
                <button
                  onClick={() => playCollection(activePlaylist.tracks || [], `playlist:${activePlaylist.id}`)}
                  disabled={!activePlaylist.tracks?.length}
                  className="rounded-full bg-[#D4A23A] px-3 py-2 text-xs font-black text-black disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Reda
                </button>
                <button
                  onClick={() => toggleCollectionShuffle(`playlist:${activePlaylist.id}`)}
                  disabled={!activePlaylist.tracks?.length}
                  className={`rounded-full px-3 py-2 text-xs font-black transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    shuffle && shuffleSource === `playlist:${activePlaylist.id}`
                      ? "bg-[#D4A23A] text-black shadow-[0_0_20px_rgba(212,162,58,0.18)] hover:bg-[#E2B85C]"
                      : "border border-[#D4A23A]/35 bg-[#D4A23A]/10 text-[#D4A23A] hover:bg-[#D4A23A] hover:text-black"
                  }`}
                >
                  Shuffle
                </button>
                <button onClick={() => deletePlaylist(activePlaylist.id)} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white">
                  <Trash2 size={14} />
                </button>
              </div>

              {!activePlaylist.tracks?.length ? (
                <div className="rounded-2xl border border-dashed border-[#D4A23A]/20 p-6 text-center text-sm text-zinc-400">
                  Apasa Playlist pe orice piesa din stanga. Se adauga aici imediat.
                </div>
              ) : (
                <div className="space-y-2">
                  {activePlaylist.tracks.map((track, i) => (
                    <TrackCard
                      key={`${activePlaylist.id}-${track.id}`}
                      track={track}
                      list={activePlaylist.tracks}
                      index={i}
                      isFavorite={favoriteIdSet.has(track.id)}
                      onToggleFavorite={toggleFavorite}
                      onRemoveFromPlaylist={() => removeFromPlaylist(activePlaylist.id, track.id)}
                      showPlaylistActions={false}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-500">
      <Icon size={25} className="mx-auto mb-3 text-zinc-600" />
      {text}
    </div>
  );
}
